import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { Button } from '@siliconx/shared/ui/Button';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import { LoadingShell, ErrorState, EmptyState } from '@siliconx/shared/ui/state';
import { qk } from '../../lib/queryKeys';
import {
  BASIS_LABEL,
  COMPETENCY_LABEL,
  closeR2Batch,
  createR2Batch,
  designateR2Dpr,
  disposeR2Unit,
  drawR2Sample,
  getR2Competency,
  getR2Dpr,
  getR2Draw,
  getR2Status,
  getR2Summary,
  listR2Batches,
  listR2Candidates,
  listR2Draws,
  listR2TeamMembers,
  listR2Units,
  r2ErrorMessage,
  receiveR2Units,
  recordR2Competency,
  recordR2Remediation,
  recordR2Result,
  releaseR2Unit,
  type R2DrawItem,
  type R2TeamMember,
} from '../../lib/r2Controls';

/**
 * SIL-2383. Partner R2 controls: receipt counts, 5% erase sampling, DPR release, operator competency and the one-year untested-unit
 * alert. Any member reads and records receipts and sample results; admin-only controls are hidden for members AND refused by the
 * definers. While the server switch or the org's enrolment is missing the API answers 404 and this page says the controls are not
 * available. Copy states records and results only - it never says anything is certified, verified or compliant.
 */
const inputCls = 'rounded-md border border-border bg-card px-2 py-1.5 text-sm';
const todayIso = () => new Date().toISOString().slice(0, 10);

function Err({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-xs text-red-700 dark:text-red-400">
      {r2ErrorMessage(error)}
    </p>
  );
}

function useTeam() {
  const team = useQuery({ queryKey: qk.r2.team(), queryFn: listR2TeamMembers, retry: false });
  const nameOf = useMemo(() => {
    const m = new Map((team.data ?? []).map((t: R2TeamMember) => [t.id, t.email]));
    return (id: string) => m.get(id) ?? 'Team member';
  }, [team.data]);
  return { members: (team.data ?? []).filter((m) => m.isActive), nameOf };
}

function useInvalidateR2() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: qk.r2.all() });
}

// ---------------------------------------------------------------------------------------------------------------------------------
export function R2AlertBanner() {
  const status = useQuery({ queryKey: qk.r2.status(), queryFn: getR2Status, retry: false });
  const summary = useQuery({ queryKey: qk.r2.summary(), queryFn: getR2Summary, retry: false, enabled: status.data?.enabled === true });
  if (!status.data?.enabled || !summary.data) return null;
  const s = summary.data;
  const parts: string[] = [];
  if (s.alerts.over > 0) parts.push(`${s.alerts.over} received unit${s.alerts.over === 1 ? '' : 's'} untested for over a year`);
  if (s.alerts.nearing > 0) parts.push(`${s.alerts.nearing} nearing one year untested`);
  if (s.samples.failedWithoutCorrectiveAction > 0) parts.push(`${s.samples.failedWithoutCorrectiveAction} failed sample result${s.samples.failedWithoutCorrectiveAction === 1 ? '' : 's'} without a corrective action`);
  if (parts.length === 0) return null;
  return (
    <InsightBanner lead="R2 controls need attention.">
      {parts.join('; ')}. <a className="underline" href="/r2-controls">Open R2 controls</a>.
    </InsightBanner>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
function CountsTab({ isAdmin }: { isAdmin: boolean }) {
  const summary = useQuery({ queryKey: qk.r2.summary(), queryFn: getR2Summary, retry: false });
  const batches = useQuery({ queryKey: qk.r2.batches(), queryFn: listR2Batches, retry: false });
  const refresh = useInvalidateR2();
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const [qty, setQty] = useState('');
  const [label, setLabel] = useState('');
  const create = useMutation({
    mutationFn: () => createR2Batch({ receivedOn, declaredQty: Number(qty), label: label.trim() === '' ? null : label.trim() }),
    onSuccess: () => { setQty(''); setLabel(''); refresh(); },
  });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <Card>
        <PanelTitle title="Declared, received and processed" description="By month received. A unit counts as processed only after a committed run from a registered bench site; runs from other locations are shown separately and never count. Chassis and GPU units only: drives and memory are not received as units." />
        {summary.isPending && <LoadingShell rows={3} />}
        {summary.isError && <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />}
        {summary.data && summary.data.months.length === 0 && <EmptyState title="No receipts recorded yet." description="Record a received batch below." />}
        {summary.data && summary.data.months.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Month</th><th>Declared</th><th>Received</th><th>Processed</th><th>Self-reported only</th><th>Outstanding</th></tr></thead>
            <tbody>
              {summary.data.months.map((m) => (
                <tr key={m.month} className="border-t border-border">
                  <td className="py-1.5">{m.month.slice(0, 7)}</td><td>{m.declared}</td><td>{m.received}</td><td>{m.processedRegistered}</td><td>{m.processedSelfReportedOnly}</td>
                  <td>{m.outstanding > 0 ? <StatusPill tone="amber">{m.outstanding}</StatusPill> : m.outstanding}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <PanelTitle title="Record a received batch" description="Declare the quantity you were told to expect, then add the units you actually received." />
        <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <label className="text-xs">Received on<br /><input className={inputCls} type="date" value={receivedOn} max={todayIso()} onChange={(e) => setReceivedOn(e.target.value)} required /></label>
          <label className="text-xs">Declared quantity<br /><input className={inputCls} type="number" min={1} max={5000} value={qty} onChange={(e) => setQty(e.target.value)} required /></label>
          <label className="text-xs">Reference (optional)<br /><input className={inputCls} maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="PO-20261029" /></label>
          <Button type="submit" size="sm" disabled={create.isPending || qty === ''}>Record batch</Button>
        </form>
        <Err error={create.error} />
      </Card>

      <Card>
        <PanelTitle title="Batches" />
        {batches.isPending && <LoadingShell rows={3} />}
        {batches.isError && <ErrorState error={batches.error} onRetry={() => void batches.refetch()} />}
        {batches.data && batches.data.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No batches yet.</p>}
        <ul className="mt-2 divide-y divide-border">
          {(batches.data ?? []).map((b) => (
            <li key={b.id} className="space-y-2 py-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{b.label ?? 'Batch'} - {b.receivedOn}</span>
                <span>{b.lines} of {b.declaredQty} units</span>
                {b.closedOutcome ? <StatusPill tone="neutral">{closedLabel(b.closedOutcome)}</StatusPill> : b.lines !== b.declaredQty ? <StatusPill tone="amber">Count differs</StatusPill> : null}
                {!b.closedOutcome && <Button size="sm" variant="secondary" onClick={() => setOpen(open === b.id ? null : b.id)}>{open === b.id ? 'Hide' : 'Add units / close'}</Button>}
              </div>
              {open === b.id && !b.closedOutcome && <BatchWork batchId={b.id} lines={b.lines} declared={b.declaredQty} isAdmin={isAdmin} onDone={refresh} />}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function closedLabel(o: string): string {
  return o === 'reconciled' ? 'Closed: count matches' : o === 'discrepancy_supplier_notified' ? 'Closed: discrepancy, supplier notified' : 'Closed: abandoned';
}

function BatchWork({ batchId, lines, declared, onDone }: { batchId: string; lines: number; declared: number; isAdmin: boolean; onDone: () => void }) {
  const cand = useQuery({ queryKey: qk.r2.candidates(''), queryFn: () => listR2Candidates(), retry: false });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const receive = useMutation({ mutationFn: () => receiveR2Units(batchId, [...picked]), onSuccess: () => { setPicked(new Set()); onDone(); } });
  const close = useMutation({ mutationFn: (outcome: 'reconciled' | 'discrepancy_supplier_notified' | 'abandoned') => closeR2Batch(batchId, outcome), onSuccess: onDone });
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const matches = lines === declared;
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <p className="text-xs text-muted-foreground">Pick units already in your inventory. Serial numbers are shown with only the last four characters.</p>
      {cand.isPending && <LoadingShell rows={2} />}
      {cand.data && cand.data.length === 0 && <p className="text-sm text-muted-foreground">Every unit in your inventory is already received.</p>}
      <ul className="max-h-48 divide-y divide-border overflow-auto text-sm">
        {(cand.data ?? []).map((u) => (
          <li key={u.assetId} className="flex items-center gap-2 py-1">
            <input type="checkbox" aria-label={`Select unit ${u.serialMasked ?? u.assetId}`} checked={picked.has(u.assetId)} onChange={() => toggle(u.assetId)} disabled={!picked.has(u.assetId) && picked.size >= 200} />
            <span>{u.serialMasked ?? 'No serial recorded'}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={picked.size === 0 || receive.isPending} onClick={() => receive.mutate()}>Receive {picked.size} unit{picked.size === 1 ? '' : 's'}</Button>
        {matches && lines > 0 && <Button size="sm" variant="secondary" onClick={() => close.mutate('reconciled')}>Close: count matches</Button>}
        {!matches && lines > 0 && <Button size="sm" variant="secondary" onClick={() => close.mutate('discrepancy_supplier_notified')}>Close: discrepancy, supplier notified</Button>}
        {lines === 0 && <Button size="sm" variant="secondary" onClick={() => close.mutate('abandoned')}>Abandon empty batch</Button>}
      </div>
      <Err error={receive.error ?? close.error} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
function SamplingTab({ isAdmin }: { isAdmin: boolean }) {
  const draws = useQuery({ queryKey: qk.r2.draws(), queryFn: listR2Draws, retry: false });
  const refresh = useInvalidateR2();
  const [month, setMonth] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const draw = useMutation({ mutationFn: () => drawR2Sample(`${month}-01`), onSuccess: (r) => { setMonth(''); refresh(); if (typeof r.drawId === 'string') setSelected(r.drawId); } });
  return (
    <div className="space-y-6">
      <Card>
        <PanelTitle title="Monthly sample of erased drives" description="R2 requires a sample of at least 5% of logically sanitized drives, checked by a competent and independent person. The draw is made by the system from the month's erase records and cannot be repeated." />
        {isAdmin ? (
          <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); draw.mutate(); }}>
            <label className="text-xs">Month (must have ended)<br /><input className={inputCls} type="month" value={month} max={todayIso().slice(0, 7)} onChange={(e) => setMonth(e.target.value)} required /></label>
            <Button type="submit" size="sm" disabled={month === '' || draw.isPending}>Draw sample</Button>
          </form>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">An organization admin draws the sample.</p>
        )}
        <Err error={draw.error} />
        {draws.isPending && <LoadingShell rows={3} />}
        {draws.isError && <ErrorState error={draws.error} onRetry={() => void draws.refetch()} />}
        {draws.data && draws.data.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No sample drawn yet.</p>}
        <ul className="mt-3 divide-y divide-border text-sm">
          {(draws.data ?? []).map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="font-medium">{d.periodStart.slice(0, 7)}</span>
              <span>{d.sampleSize} of {d.populationCount} drives ({d.samplePct}%)</span>
              <span>{d.resultCount} of {d.sampleSize} results</span>
              {d.failedCount > 0 && <StatusPill tone="red">{d.failedCount} failed</StatusPill>}
              <Button size="sm" variant="secondary" onClick={() => setSelected(selected === d.id ? null : d.id)}>{selected === d.id ? 'Hide' : 'Open'}</Button>
            </li>
          ))}
        </ul>
      </Card>
      {selected && <DrawDetail id={selected} isAdmin={isAdmin} />}
    </div>
  );
}

function DrawDetail({ id, isAdmin }: { id: string; isAdmin: boolean }) {
  const detail = useQuery({ queryKey: qk.r2.draw(id), queryFn: () => getR2Draw(id), retry: false });
  if (detail.isPending) return <LoadingShell rows={3} />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  const d = detail.data.draw;
  return (
    <Card>
      <PanelTitle title={`Sample for ${d.periodStart.slice(0, 7)}`} description={`${d.sampleSize} of ${d.populationCount} drives. ${detail.data.evidenceChecked.systemChecked} of ${detail.data.evidenceChecked.resulted} results have evidence the system checked.`} />
      <details className="mt-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer">How this sample was drawn</summary>
        <p className="mt-1">Drives are ranked by SHA-256 of the seed and the erase record id; the first {d.sampleSize} are selected. Population fingerprint {d.populationSha256}. Seed {d.seedHex ?? 'not available'}. Method version {d.algorithmVersion}, population rule {d.populationRuleVersion}. Population cut off at {d.populationCutoffAt ?? 'unknown'}.</p>
      </details>
      <ul className="mt-3 divide-y divide-border">
        {detail.data.items.map((it) => <SampleItem key={it.certificateId} drawId={id} item={it} isAdmin={isAdmin} />)}
      </ul>
    </Card>
  );
}

function SampleItem({ drawId, item, isAdmin }: { drawId: string; item: R2DrawItem; isAdmin: boolean }) {
  const refresh = useInvalidateR2();
  const [outcome, setOutcome] = useState('pass');
  const [method, setMethod] = useState('read_back_check');
  const [verifier, setVerifier] = useState('internal_independent');
  const [on, setOn] = useState(todayIso());
  const [evidence, setEvidence] = useState('');
  const [rerun, setRerun] = useState('');
  const [note, setNote] = useState('');
  const [fixNote, setFixNote] = useState('');
  const save = useMutation({
    mutationFn: () => recordR2Result(drawId, {
      certificateId: item.certificateId, outcome, method, verifierKind: verifier, verifiedOn: on,
      evidenceSha256: evidence.trim() === '' ? null : evidence.trim(), rerunRunId: method === 'tool_rerun' && rerun.trim() !== '' ? rerun.trim() : null, note: note.trim() === '' ? null : note.trim(),
    }),
    onSuccess: refresh,
  });
  const fix = useMutation({ mutationFn: () => recordR2Remediation(drawId, { certificateId: item.certificateId, note: fixNote.trim() === '' ? null : fixNote.trim() }), onSuccess: refresh });
  const failed = item.outcome === 'fail' || item.outcome === 'not_located';
  return (
    <li className="space-y-2 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{item.model ?? 'Drive'} {item.serialMasked ?? ''}</span>
        <span className="text-xs text-muted-foreground">erased {item.erasedAt?.slice(0, 10) ?? 'unknown'}</span>
        {item.outcome ? <StatusPill tone={failed ? 'red' : 'green'}>{item.outcome === 'pass' ? 'Result: pass' : item.outcome === 'fail' ? 'Result: fail' : 'Result: not located'} ({item.method?.replace(/_/g, ' ')})</StatusPill> : <StatusPill tone="amber">Result needed</StatusPill>}
        {failed && item.correctiveActionRecorded && <StatusPill tone="neutral">Corrective action recorded</StatusPill>}
      </div>
      {!item.outcome && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <label className="text-xs">Outcome<br /><select className={inputCls} value={outcome} onChange={(e) => setOutcome(e.target.value)}><option value="pass">Pass</option><option value="fail">Fail</option><option value="not_located">Not located</option></select></label>
          <label className="text-xs">Method<br /><select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}><option value="read_back_check">Read-back check</option><option value="tool_rerun">Re-run on a registered bench</option><option value="third_party_report">Third-party report</option></select></label>
          <label className="text-xs">Done by<br /><select className={inputCls} value={verifier} onChange={(e) => setVerifier(e.target.value)}><option value="internal_independent">Independent colleague</option><option value="third_party">Third party</option></select></label>
          <label className="text-xs">Date<br /><input className={inputCls} type="date" value={on} max={todayIso()} onChange={(e) => setOn(e.target.value)} required /></label>
          {method === 'third_party_report' && <label className="text-xs">Report SHA-256<br /><input className={inputCls} value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={64} required /></label>}
          {method === 'tool_rerun' && <label className="text-xs">Re-run id<br /><input className={inputCls} value={rerun} onChange={(e) => setRerun(e.target.value)} required /></label>}
          <label className="text-xs">Note (optional)<br /><input className={inputCls} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} /></label>
          <Button type="submit" size="sm" disabled={save.isPending}>Record result</Button>
        </form>
      )}
      <Err error={save.error} />
      {failed && !item.correctiveActionRecorded && isAdmin && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); fix.mutate(); }}>
          <label className="text-xs">Corrective action taken<br /><input className={inputCls} value={fixNote} maxLength={200} onChange={(e) => setFixNote(e.target.value)} /></label>
          <Button type="submit" size="sm" variant="secondary" disabled={fix.isPending}>Record corrective action</Button>
        </form>
      )}
      <Err error={fix.error} />
    </li>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
function DprTab({ isAdmin }: { isAdmin: boolean }) {
  const dpr = useQuery({ queryKey: qk.r2.dpr(), queryFn: getR2Dpr, retry: false });
  const units = useQuery({ queryKey: qk.r2.units(), queryFn: listR2Units, retry: false });
  const { members, nameOf } = useTeam();
  const refresh = useInvalidateR2();
  const [who, setWho] = useState('');
  const designate = useMutation({ mutationFn: (v: { id: string; action: 'designate' | 'revoke' }) => designateR2Dpr(v.id, v.action), onSuccess: () => { setWho(''); refresh(); } });
  const isDpr = (dpr.data?.dprs ?? []).some((d) => d.isYou);

  const [asset, setAsset] = useState('');
  const [basis, setBasis] = useState('all_data_devices_sanitized');
  const [reason, setReason] = useState('');
  const release = useMutation({ mutationFn: () => releaseR2Unit({ assetId: asset, basis, reasonCode: basis === 'all_data_devices_sanitized' || reason === '' ? null : reason }), onSuccess: () => { setAsset(''); refresh(); } });
  const releasable = (units.data ?? []).filter((u) => !u.releaseBasis);
  const reasons = basis === 'no_data_device' ? [['no_storage_present', 'No storage present'], ['storage_removed_before_receipt', 'Storage removed before receipt']] : [['physically_destroyed', 'Physically destroyed'], ['shredded', 'Shredded'], ['degaussed', 'Degaussed']];

  return (
    <div className="space-y-6">
      <Card>
        <PanelTitle title="Data Protection Representatives" description="A designated Data Protection Representative approves data storage devices for release. Nobody can designate themselves: another admin designates you, or SiliconX support does for an organization with a single admin." />
        {dpr.isPending && <LoadingShell rows={2} />}
        {dpr.isError && <ErrorState error={dpr.error} onRetry={() => void dpr.refetch()} />}
        {dpr.data && dpr.data.dprs.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No representative designated yet.</p>}
        <ul className="mt-2 divide-y divide-border text-sm">
          {(dpr.data?.dprs ?? []).map((d) => (
            <li key={d.designeeId} className="flex flex-wrap items-center gap-2 py-2">
              <span className="font-medium">{nameOf(d.designeeId)}{d.isYou ? ' (you)' : ''}</span>
              <span className="text-xs text-muted-foreground">since {d.designatedAt?.slice(0, 10)}</span>
              {d.opsDesignated && <StatusPill tone="neutral">Designated by SiliconX support</StatusPill>}
              {isAdmin && <Button size="sm" variant="secondary" onClick={() => designate.mutate({ id: d.designeeId, action: 'revoke' })}>Revoke</Button>}
            </li>
          ))}
        </ul>
        {isAdmin && (
          <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); designate.mutate({ id: who, action: 'designate' }); }}>
            <label className="text-xs">Designate<br />
              <select className={inputCls} value={who} onChange={(e) => setWho(e.target.value)} required>
                <option value="">Choose a team member</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}
              </select>
            </label>
            <Button type="submit" size="sm" disabled={who === '' || designate.isPending}>Designate</Button>
          </form>
        )}
        <Err error={designate.error} />
      </Card>

      <Card>
        <PanelTitle title="Release data storage devices" description="Record the Data Protection Representative's approval of a received unit. The system checks the basis against what the bench run found; a release is a facility record and does not change the passport." />
        {!isDpr ? (
          <p className="mt-2 text-sm text-muted-foreground">Only a designated Data Protection Representative can record a release.</p>
        ) : (
          <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); release.mutate(); }}>
            <label className="text-xs">Unit<br />
              <select className={inputCls} value={asset} onChange={(e) => setAsset(e.target.value)} required>
                <option value="">Choose a received unit</option>
                {releasable.map((u) => <option key={u.assetId} value={u.assetId}>{u.serialMasked ?? 'No serial'} - received {u.receivedOn}</option>)}
              </select>
            </label>
            <label className="text-xs">Basis<br />
              <select className={inputCls} value={basis} onChange={(e) => { setBasis(e.target.value); setReason(''); }}>
                {Object.entries(BASIS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            {basis !== 'all_data_devices_sanitized' && (
              <label className="text-xs">Reason<br />
                <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} required>
                  <option value="">Choose</option>
                  {reasons.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
            )}
            <Button type="submit" size="sm" disabled={asset === '' || release.isPending}>Record release</Button>
          </form>
        )}
        <Err error={release.error} />
        {(dpr.data?.releases ?? []).length > 0 && (
          <ul className="mt-3 divide-y divide-border text-sm">
            {dpr.data?.releases.map((r) => <li key={r.assetId} className="py-1.5">Release recorded by designated representative - {BASIS_LABEL[r.basis] ?? r.basis} ({r.releasedAt?.slice(0, 10)})</li>)}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
function CompetencyTab({ isAdmin }: { isAdmin: boolean }) {
  const roster = useQuery({ queryKey: qk.r2.competency(), queryFn: getR2Competency, retry: false });
  const { members, nameOf } = useTeam();
  const refresh = useInvalidateR2();
  const [user, setUser] = useState('');
  const [competency, setCompetency] = useState('bench_operation');
  const [action, setAction] = useState<'grant' | 'revoke'>('grant');
  const [trained, setTrained] = useState(todayIso());
  const [expires, setExpires] = useState('');
  const [trainerKind, setTrainerKind] = useState<'internal_user' | 'external_provider'>('external_provider');
  const [trainer, setTrainer] = useState('');
  const [provider, setProvider] = useState('');
  const save = useMutation({
    mutationFn: () =>
      recordR2Competency(action === 'revoke'
        ? { userId: user, competency, action }
        : { userId: user, competency, action, trainedOn: trained, expiresOn: expires === '' ? null : expires, trainerKind,
            trainerUserId: trainerKind === 'internal_user' ? trainer : null, providerRef: trainerKind === 'external_provider' ? provider.trim() : null }),
    onSuccess: () => { setUser(''); refresh(); },
  });
  const rows = roster.data ?? [];
  const people = [...new Set(rows.map((r) => r.userId))];
  return (
    <div className="space-y-6">
      <Card>
        <PanelTitle title="Operator competency" description="Who has a recorded, current competency for bench operation, data sanitization and verification, and Data Protection Representative duties. A record is the organization's own statement of training; it is not an assessment by SiliconX." />
        {roster.isPending && <LoadingShell rows={3} />}
        {roster.isError && <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />}
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Person</th>{Object.values(COMPETENCY_LABEL).map((l) => <th key={l}>{l}</th>)}</tr></thead>
          <tbody>
            {people.map((p) => (
              <tr key={p} className="border-t border-border">
                <td className="py-1.5">{nameOf(p)}</td>
                {Object.keys(COMPETENCY_LABEL).map((k) => {
                  const r = rows.find((x) => x.userId === p && x.competency === k);
                  return (
                    <td key={k}>
                      {r?.competent ? <StatusPill tone="green">Current</StatusPill> : <span className="text-xs text-muted-foreground">None</span>}
                      {r?.competent && r.expiresOn && <span className="ml-1 text-xs text-muted-foreground">to {r.expiresOn}</span>}
                      {r?.competent && r.selfRecorded && <span className="ml-1 text-xs text-muted-foreground">(self-recorded)</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {isAdmin && (
        <Card>
          <PanelTitle title="Record or revoke" />
          <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <label className="text-xs">Person<br /><select className={inputCls} value={user} onChange={(e) => setUser(e.target.value)} required><option value="">Choose</option>{members.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}</select></label>
            <label className="text-xs">Competency<br /><select className={inputCls} value={competency} onChange={(e) => setCompetency(e.target.value)}>{Object.entries(COMPETENCY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
            <label className="text-xs">Action<br /><select className={inputCls} value={action} onChange={(e) => setAction(e.target.value as 'grant' | 'revoke')}><option value="grant">Record competency</option><option value="revoke">Revoke</option></select></label>
            {action === 'grant' && (
              <>
                <label className="text-xs">Trained on<br /><input className={inputCls} type="date" value={trained} max={todayIso()} onChange={(e) => setTrained(e.target.value)} required /></label>
                <label className="text-xs">Expires (optional)<br /><input className={inputCls} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} /></label>
                <label className="text-xs">Trainer<br /><select className={inputCls} value={trainerKind} onChange={(e) => setTrainerKind(e.target.value as 'internal_user' | 'external_provider')}><option value="external_provider">External provider</option><option value="internal_user">Colleague</option></select></label>
                {trainerKind === 'internal_user'
                  ? <label className="text-xs">Colleague<br /><select className={inputCls} value={trainer} onChange={(e) => setTrainer(e.target.value)} required><option value="">Choose</option>{members.filter((m) => m.id !== user).map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}</select></label>
                  : <label className="text-xs">Provider code<br /><input className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)} pattern="[a-z0-9_-]{2,40}" placeholder="acme-training" required /></label>}
              </>
            )}
            <Button type="submit" size="sm" disabled={user === '' || save.isPending}>{action === 'grant' ? 'Record' : 'Revoke'}</Button>
          </form>
          <Err error={save.error} />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
function AlertsTab({ isAdmin }: { isAdmin: boolean }) {
  const summary = useQuery({ queryKey: qk.r2.summary(), queryFn: getR2Summary, retry: false });
  const refresh = useInvalidateR2();
  const [target, setTarget] = useState<string | null>(null);
  const [kind, setKind] = useState('returned_to_owner');
  const [reason, setReason] = useState('owner_request');
  const [on, setOn] = useState(todayIso());
  const [hash, setHash] = useState('');
  const save = useMutation({
    mutationFn: () => disposeR2Unit(target as string, { kind, reasonCode: reason, disposedOn: on, evidenceSha256: kind === 'tested_elsewhere' && hash.trim() !== '' ? hash.trim() : null }),
    onSuccess: () => { setTarget(null); refresh(); },
  });
  if (summary.isPending) return <LoadingShell rows={3} />;
  if (summary.isError) return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  const items = summary.data.alerts.items;
  return (
    <Card>
      <PanelTitle title="Units not tested within a year" description="R2 expects equipment to be tested, repaired or refurbished within one year of receipt, or evaluated and inventoried. A unit leaves this list when a committed run from a registered bench exists after receipt, or when an organization admin records what happened to it." />
      {items.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No received unit is within 30 days of the one-year mark.</p>}
      <ul className="mt-3 divide-y divide-border text-sm">
        {items.map((u) => (
          <li key={u.receiptId} className="space-y-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={u.band === 'over' ? 'red' : 'amber'}>{u.band === 'over' ? 'Over one year' : 'Nearing one year'}</StatusPill>
              <span>Received {u.receivedOn} - {u.daysSince} days</span>
              {isAdmin && <Button size="sm" variant="secondary" onClick={() => setTarget(target === u.assetId ? null : u.assetId)}>Record disposition</Button>}
            </div>
            {target === u.assetId && (
              <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                <label className="text-xs">What happened<br /><select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="returned_to_owner">Returned to owner</option><option value="tested_elsewhere">Tested elsewhere (report hash needed)</option>
                  <option value="released_untested">Released untested (Data Protection Representative)</option><option value="recycled_untested">Recycled untested (Data Protection Representative)</option></select></label>
                <label className="text-xs">Reason<br /><select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="owner_request">Owner request</option><option value="customer_return">Customer return</option><option value="scrap_non_functional">Scrap, non-functional</option>
                  <option value="sold_as_is_documented">Sold as-is, documented</option><option value="external_test_report">External test report</option></select></label>
                <label className="text-xs">Date<br /><input className={inputCls} type="date" value={on} min={u.receivedOn} max={todayIso()} onChange={(e) => setOn(e.target.value)} required /></label>
                {kind === 'tested_elsewhere' && <label className="text-xs">Report SHA-256<br /><input className={inputCls} value={hash} maxLength={64} onChange={(e) => setHash(e.target.value)} required /></label>}
                <Button type="submit" size="sm" disabled={save.isPending}>Save</Button>
              </form>
            )}
          </li>
        ))}
      </ul>
      <Err error={save.error} />
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
type Tab = 'counts' | 'sampling' | 'dpr' | 'competency' | 'alerts';
const TABS: Array<[Tab, string]> = [['counts', 'Counts'], ['sampling', 'Sampling'], ['dpr', 'Release'], ['competency', 'Competency'], ['alerts', 'Alerts']];

export function R2ControlsView({ isAdminOverride }: { isAdminOverride?: boolean } = {}): ReactNode {
  const status = useQuery({ queryKey: qk.r2.status(), queryFn: getR2Status, retry: false });
  const [tab, setTab] = useState<Tab>('counts');
  const summary = useQuery({ queryKey: qk.r2.summary(), queryFn: getR2Summary, retry: false, enabled: status.data?.enabled === true });

  if (status.isPending) return <LoadingShell rows={4} />;
  if (status.isError) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  if (!status.data.enabled) {
    return (
      <div className="space-y-6">
        <InsightBanner tone="neutral" lead="R2 controls are not available.">
          They are turned on per organization during the partner pilot. Contact SiliconX support to enrol your organization.
        </InsightBanner>
      </div>
    );
  }
  const isAdmin = isAdminOverride ?? status.data.isAdmin;
  const alerts = summary.data ? summary.data.alerts.over + summary.data.alerts.nearing : 0;
  return (
    <div className="space-y-6">
      <R2AlertBanner />
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-sm ${tab === k ? 'border-b-2 border-brand-600 font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
            {label}{k === 'alerts' && alerts > 0 ? ` (${alerts})` : ''}
          </button>
        ))}
      </div>
      {tab === 'counts' && <CountsTab isAdmin={isAdmin} />}
      {tab === 'sampling' && <SamplingTab isAdmin={isAdmin} />}
      {tab === 'dpr' && <DprTab isAdmin={isAdmin} />}
      {tab === 'competency' && <CompetencyTab isAdmin={isAdmin} />}
      {tab === 'alerts' && <AlertsTab isAdmin={isAdmin} />}
    </div>
  );
}
